import { apiJsonRequest } from './apiClient'

export type EligibilityIntegrationProvider = 'stedi' | 'pverify'

export type CreateEligibilityCheckPayload = {
  integration_provider: EligibilityIntegrationProvider
  patient_id?: string
  payor_id?: string
  patient_intake_id?: string
  status?: string
  stedi_api_date?: string
  request_payload: Record<string, unknown>
  response_payload?: Record<string, unknown>
  api_date?: string
  upstream_status?: number
  upstream_url?: string
  response?: Record<string, unknown>
}

export type ApiEligibilityCheck = {
  id?: string
  patient_id?: string
  payor_id?: string
  patient_intake_id?: string
  integration_provider?: string
  status?: string
  checked_at?: string
} & Record<string, unknown>

type EligibilityChecksListResponse =
  | ApiEligibilityCheck[]
  | {
      data?: ApiEligibilityCheck[] | { items?: ApiEligibilityCheck[]; results?: ApiEligibilityCheck[] }
      items?: ApiEligibilityCheck[]
      results?: ApiEligibilityCheck[]
    }

function unwrapData<T extends Record<string, unknown>>(body: T | { data?: T }): T {
  if (body && typeof body === 'object' && 'data' in body && body.data && typeof body.data === 'object') {
    return body.data as T
  }
  return body as T
}

function asEligibilityChecksArray(body: EligibilityChecksListResponse): ApiEligibilityCheck[] {
  if (Array.isArray(body)) return body
  if (!body || typeof body !== 'object') return []

  if (Array.isArray(body.items)) return body.items
  if (Array.isArray(body.results)) return body.results

  const data = body.data
  if (Array.isArray(data)) return data
  if (data && typeof data === 'object') {
    if (Array.isArray(data.items)) return data.items
    if (Array.isArray(data.results)) return data.results
  }
  return []
}

export async function createEligibilityCheck(
  payload: CreateEligibilityCheckPayload
): Promise<ApiEligibilityCheck> {
  const response = await apiJsonRequest<ApiEligibilityCheck | { data?: ApiEligibilityCheck }>(
    '/eligibility-checks',
    {
      method: 'POST',
      body: JSON.stringify(payload),
    },
    { debugLabel: 'eligibility-checks-create' }
  )
  return unwrapData(response)
}

export async function listEligibilityChecks(): Promise<ApiEligibilityCheck[]> {
  const response = await apiJsonRequest<EligibilityChecksListResponse>(
    '/eligibility-checks',
    { method: 'GET' },
    { debugLabel: 'eligibility-checks-list' }
  )
  return asEligibilityChecksArray(response)
}

export async function getEligibilityCheck(eligibilityCheckId: string): Promise<ApiEligibilityCheck> {
  const response = await apiJsonRequest<ApiEligibilityCheck | { data?: ApiEligibilityCheck }>(
    `/eligibility-checks/${encodeURIComponent(eligibilityCheckId)}`,
    { method: 'GET' },
    { debugLabel: 'eligibility-checks-get' }
  )
  return unwrapData(response)
}
